import type { ScriptChoice, ScriptConversion } from "@kensio/pinyinjs";

import type { PanelTarget } from "./script-panels.js";

/**
 * What the conversions had to decide, gathered across the panels.
 *
 * Simplification merged characters, so going back out of 简体 means picking
 * between forms that one character now stands for: 发 is 發 or 髮, 干 is 干 or
 * 乾 or 幹. Most converters resolve those from a phrase list and are silently
 * wrong past the end of it. This one knows what settled each of them, which is
 * the thing a teacher checking a worksheet wants and the thing a learner
 * should see before pasting the result into an essay.
 *
 * Kept apart from `script-choices.ts`, which puts these on the page: which
 * decisions there were is a question about the conversions, and answering it
 * needs no DOM.
 */

/** One conversion, and the orthography it was written in. */
export interface WrittenScript {
  readonly target: PanelTarget;
  readonly conversion: ScriptConversion;
}

/** One decision, and which of the scripts on the page came out of it that way. */
export interface NotableChoice {
  readonly choice: ScriptChoice;
  readonly targets: readonly PanelTarget[];
}

/** One row while it is still being built up, before its scripts are all in. */
interface Decision {
  readonly choice: ScriptChoice;
  readonly targets: PanelTarget[];
}

/**
 * The rows, gathered a script at a time.
 *
 * The map is the lookup and the array is the order, and both hold the same
 * objects: a script joining a decision that is already listed pushes onto the
 * row that is already in the array, so a row's scripts fill in as the panels
 * are folded in rather than after all of them are known.
 */
class Decisions {
  readonly #byDecision = new Map<string, Decision>();
  readonly #rows: Decision[] = [];

  /**
   * Fold one script's conversion in.
   *
   * A decision already listed gains this script rather than a second row, so
   * that the common case — every panel deciding a character the same way —
   * stays one line.
   *
   * The characters with only one form never reach the loop. They are the great
   * majority, and a list that included 们 becoming 們 would bury the three
   * lines worth reading.
   */
  add({ target, conversion }: WrittenScript): void {
    const decided = conversion.choices.filter(
      (choice) => choice.evidence !== "locked",
    );

    for (const choice of decided) {
      const key = `${choice.from}>${choice.to}:${choice.evidence}`;
      const already = this.#byDecision.get(key);

      if (already === undefined) {
        const row = { choice, targets: [target] };
        this.#byDecision.set(key, row);
        this.#rows.push(row);
      } else if (!already.targets.includes(target)) {
        already.targets.push(target);
      }
    }
  }

  get rows(): readonly NotableChoice[] {
    return this.#rows;
  }
}

/**
 * The choices worth showing, each of them once.
 *
 * Deduplicated, because a page of Chinese will convert 发 twenty times and the
 * list is about which characters had a decision in them rather than about how
 * often each came up. Two conversions of the same character stay apart where
 * they came out differently or were settled differently, which is exactly the
 * pair worth seeing: 头发 is 頭髮 by its reading and 出发 is 出發 by default.
 *
 * Taken across every script the page is showing rather than one of them, and
 * each row remembers which of them it came out of. The three panels mostly
 * agree about what had to be decided, so one row usually stands for all three;
 * where they disagree — 面 is 麵 in Taipei and 麪 in Hong Kong — both rows
 * survive, and each says which panel it is about, because two rows reading
 * "settled by the word it is in" and differing only in the character would
 * otherwise look like the converter contradicting itself.
 */
export function notableChoices(
  written: readonly WrittenScript[],
): readonly NotableChoice[] {
  const decisions = new Decisions();

  for (const script of written) {
    decisions.add(script);
  }

  return decisions.rows;
}
